import { Layout, Menu } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import { allRoutes, useAllRoutes } from "../routes/AppRoutes";
import {
  changeCurrentMenu,
  setCollapsedSide,
} from "../redux/auth/navCountSlice";
import { notificationRefresh } from "../redux/auth/cartRefreshSlice";
import { ReactComponent as Logo } from "../logo.svg";
import AppLogo from "./AppLogo";

const { Sider } = Layout;

export const clearStoredPaginationAndFilters = () => {
  Object.keys(sessionStorage).forEach((key) => {
    if (
      !key.includes("notification") &&
      (key.startsWith("filters:") || key.startsWith("pagination:"))
    ) {
      sessionStorage.removeItem(key);
    }
  });
};

const SideBar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { role_id } = useSelector((state) => state.auth);
  const { currentMenu } = useSelector((state) => state.navCounts);
  const [collapsed, setCollapsed] = useState(
    localStorage.getItem("collapsed") === "true" ? true : false,
  );

  const allRoutes = useAllRoutes();
  const ignoreRefresh =
    allRoutes
      .filter((route) => route.ignoreNotificationRefresh)
      .map((route) => route.path) || [];
  const Menuitems = allRoutes
    .filter((route) => route.sidebar)
    .map((route, index) => ({ key: `${index + 1}`, ...route }));

  const currentMenuItem = Menuitems.find(
    (item) =>
      item.path === pathname ||
      item.sidebar.additionalPaths?.some((path) => pathname.includes(path)),
  );

  const handleMenuClick = ({ key }) => {
    const selectedItem = Menuitems.find((item) => item.key === key);
    if (selectedItem?.path) {
      navigate(selectedItem.path);
    }
  };

  const MenuitemsData = Menuitems.filter((item) =>
    item.roles?.includes(role_id),
  ).map((item) => ({
    key: item.key,
    icon: (
      <item.sidebar.icon
        fill={item.key === currentMenuItem?.key ? "#A33434" : "#8c8c8c"}
        style={{
          height: "20px",
          width: "20px",
          color: item.key === currentMenuItem?.key ? "#A33434" : "#8c8c8c",
        }}
      />
    ),
    label: (
      <span
        className={`transition-colors text-[16px] ${
          item.key === currentMenuItem?.key ? "font-semibold" : ""
        }`}
      >
        {item.sidebar.label}
      </span>
    ),
  }));

  useEffect(() => {
    localStorage.setItem("collapsed", collapsed);
  }, [collapsed]);

  useEffect(() => {
    const handleWindowUnload = () => {
      clearStoredPaginationAndFilters();
    };

    window.addEventListener("beforeunload", handleWindowUnload);
    window.addEventListener("pagehide", handleWindowUnload);

    return () => {
      window.removeEventListener("beforeunload", handleWindowUnload);
      window.removeEventListener("pagehide", handleWindowUnload);
    };
  }, []);

  useEffect(() => {
    if (currentMenuItem?.key !== currentMenu) {
      if (!ignoreRefresh.includes(pathname)) {
        dispatch(notificationRefresh());
      }
      dispatch(changeCurrentMenu(currentMenuItem?.key));
      clearStoredPaginationAndFilters();
    }
  }, [dispatch, currentMenuItem?.key, currentMenu]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={220}
      className="bg-white custom-sider"
      trigger={null} // disable default trigger
    >
      {/* Logo */}
      <div className="flex justify-center items-center h-fit bg-white p-5">
        <AppLogo
          variant="colored"
          divClassName={`${collapsed ? "h-6" : "!w-32"} transition-all`}
        />
      </div>

      {/* Menu */}
      <Menu
        mode="inline"
        selectedKeys={[currentMenuItem?.key]}
        items={MenuitemsData}
        onClick={handleMenuClick}
        className="border-r-0 text-xl"
      />

      <div className="absolute bottom-4 w-full flex justify-center">
        <button
          onClick={() => {
            setCollapsed((prev) => !prev);
            dispatch(setCollapsedSide(collapsed));
          }}
          className={`flex items-center justify-center transition-all duration-300 
      ${collapsed ? "w-8 h-8 rounded-full" : "w-[60%] h-8 rounded-full"}`}
        >
          {collapsed ? <AiOutlineRight /> : <AiOutlineLeft />}
        </button>
      </div>
    </Sider>
  );
};

export default SideBar;
